import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import {
    FACTORING_LOADPAY,
    ProductNameFields,
    ProductNames,
} from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import {
    MoreInfoType,
    NeedsMoreInfoPreviewResponse,
} from '@/components/shared/api/endpoints/applicantInformationRequests';
import {
    useCancelInformationRequestMutation,
    useSubmitApplicationStatusMutation,
    useSubmitNeedsMoreInfoMutation,
    useSubmitNeedsMoreInfoPreviewMutation,
    useSubmitRequestInfoMutation,
} from '@/components/shared/api/mutations/applicant';
import { SideDrawer } from '@/components/shared/components/SideDrawer';
import { formatDateWithTimezone } from '@/components/shared/utils/utils';
import { withRetry } from '@/components/shared/utils/withRetry';
import { useQueryClient } from '@tanstack/react-query';
import {
    Button,
    ButtonSizes,
    ButtonVariants,
    Checkbox,
    LoadingIndicator,
    Modal,
} from '@triumph/design-system';
import DOMPurify from 'dompurify';
import { Button as PrimeButton } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import {
    DecisionDetailStatuses,
    FactoringProduct,
    FuelProduct,
    InformationRequest,
    LoadPayProduct,
} from '../../../Applicant.types';
import { getProductLabel } from '../../ApplicantConditions/ApplicantConditions.utils';
import { EmailPreviewBody, EmailPreviewHeaders } from '../ApplicantStatus.StyledComponents';
import { EmailPreviewFrame } from '@/components/shared/components/EmailPreviewFrame';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import {
    ActiveRequestCard,
    ActiveRequestDate,
    ActiveRequestMessage,
    ActiveRequestMeta,
    ActiveRequestProductTag,
    ActiveRequestsSection,
    DeclinedProductWarning,
    DeclinedProductWarningMeta,
    DeclinedProductWarningRow,
    EditMessageLink,
    FieldError,
    HidePreviewLink,
    MessageTextArea,
    PreviewSection,
    PreviewSectionHeader,
    ProductSection,
    ReadOnlyEmailRow,
    RequirementLabel,
    RequirementList,
    RequirementRow,
    SectionHeader,
    SectionHeaderRow,
    SectionLabel,
    SelectAllLink,
    SelectedCountBadge,
    SendNewHeader,
    StalePreviewBadge,
    StatusBadge,
} from './RequestInfoDrawer.styles';
import {
    buildPlaceholderValues,
    PlaceholderValues,
    renderPlaceholders,
    RequirementId,
    RequirementStatus,
    ResolvedRequirement,
    useRequirementsCatalog,
} from './requirements';

type SelectAllState = 'all' | 'invalid-only';

const NON_VALID_PRESELECT_STATUSES: ReadonlySet<RequirementStatus> = new Set([
    'missing',
    'invalid',
    'expired',
    'mismatch',
]);

const STATUS_LABEL: Record<RequirementStatus, string> = {
    valid: 'Valid',
    missing: 'Missing',
    invalid: 'Invalid',
    expired: 'Expired',
    mismatch: 'Mismatch',
    unknown: '',
};

const STATUS_TONE: Record<RequirementStatus, 'red' | 'green' | 'none'> = {
    // 'valid' is intentionally hidden — Product preferred not to show a label
    // for the positive case (felt vague). Status badges as a whole are also
    // gated off for v1 via useRequirementsCatalog; this only matters once
    // they're re-enabled, but we want this baked in for whenever that happens.
    valid: 'none',
    missing: 'red',
    invalid: 'red',
    expired: 'red',
    mismatch: 'red',
    unknown: 'none',
};

const buildBulletText = (
    selected: ReadonlyArray<RequirementId>,
    catalog: ReadonlyArray<ResolvedRequirement>,
    placeholderValues: PlaceholderValues,
): string => {
    const selectedSet = new Set(selected);
    const bullets = catalog
        .filter((r) => selectedSet.has(r.id))
        .map((r) => `• ${renderPlaceholders(r.bulletText, placeholderValues)}`);
    return bullets.join('\n\n');
};

const computeInvalidPreselected = (catalog: ReadonlyArray<ResolvedRequirement>): RequirementId[] =>
    catalog.filter((r) => NON_VALID_PRESELECT_STATUSES.has(r.status)).map((r) => r.id);

interface RequestInfoDrawerProps {
    showDrawer: boolean;
    setShowDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    getHasEverBeenApproved: (product: ProductNames) => boolean;
    getHasEverBeenSubmitted: (product: ProductNames) => boolean;
    getIsCurrentlyDeclined: (product: ProductNames) => boolean;
    defaultProduct?: string;
    isV2InformationRequestEnabled?: boolean;
    isV2InformationRequestPreApprovalEnabled?: boolean;
    isCancelInformationRequestEnabled?: boolean;
    activeRequests?: InformationRequest[];
}

export const RequestInfoDrawer = ({
    showDrawer,
    setShowDrawer,
    getHasEverBeenApproved,
    getHasEverBeenSubmitted,
    getIsCurrentlyDeclined,
    defaultProduct,
    isV2InformationRequestEnabled = false,
    isV2InformationRequestPreApprovalEnabled = false,
    isCancelInformationRequestEnabled = false,
    activeRequests = [],
}: RequestInfoDrawerProps) => {
    const { applicant, applicantId, applicantProductNames } = useApplicantContext();
    const queryClient = useQueryClient();

    const {
        requirements: resolvedCatalog,
        isLoading: isCatalogLoading,
        isError: isCatalogError,
        refetch: refetchCatalog,
    } = useRequirementsCatalog(applicant);

    const { control, formState, handleSubmit, reset, setValue, trigger, watch } = useFormContext<{
        product: string[];
        notes: string;
        docsOnly: boolean;
    }>();

    const selectedProducts = watch('product', []);
    const selectedProductsKey = (selectedProducts ?? []).join(',');
    const placeholderValues = useMemo(
        () => buildPlaceholderValues(applicant, selectedProductsKey ? selectedProductsKey.split(',') : []),
        [applicant, selectedProductsKey],
    );
    const docsOnly = watch('docsOnly', false);

    const {
        field: { value: notes, onChange: onNotesChange },
        fieldState: { error: notesError },
    } = useController({ control, name: 'notes', defaultValue: '' });
    const {
        fieldState: { error: productError },
    } = useController({ control, name: 'product' });

    const { isSafeEmailViewsEnabled } = useFeatureFlags();
    const [selectedRequirements, setSelectedRequirements] = useState<RequirementId[]>([]);
    const [isMessageEditable, setIsMessageEditable] = useState(false);
    const [previewResponse, setPreviewResponse] = useState<NeedsMoreInfoPreviewResponse | null>(
        null,
    );
    const [lastPreviewedNotes, setLastPreviewedNotes] = useState<string | null>(null);
    // Tracks whether the user has opened the preview during this drawer session.
    // Stays true after the response is cleared (e.g., when notes go empty) so
    // the auto-rerun effect can re-fetch once notes become non-empty again.
    const [wasPreviewOpened, setWasPreviewOpened] = useState(false);
    const selectAllStateRef = useRef<SelectAllState>('invalid-only');
    const [pendingCheckboxToggle, setPendingCheckboxToggle] = useState<RequirementId | null>(null);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    // True only after the user has touched the form themselves. The auto
    // pre-selection on open (defaultProduct, invalid-status requirements) is
    // not counted, so closing an untouched drawer skips the discard confirm.
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const {
        mutateAsync: submitNeedsMoreInfoPreview,
        isPending: isSubmitNeedsMoreInfoPreviewPending,
    } = useSubmitNeedsMoreInfoPreviewMutation();

    const { mutateAsync: submitNeedsMoreInfo, isPending: isSubmitNeedsMoreInfoPending } =
        useSubmitNeedsMoreInfoMutation();

    const { mutateAsync: submitRequestInfo, isPending: isSubmitRequestInfoPending } =
        useSubmitRequestInfoMutation();

    const { mutateAsync: submitApplicationStatus, isPending: isSubmitApplicationStatusPending } =
        useSubmitApplicationStatusMutation();

    const { mutate: cancelInformationRequest, isPending: isCancelingInformationRequest } =
        useCancelInformationRequestMutation();

    const [pendingCancelRequestId, setPendingCancelRequestId] = useState<string | null>(null);
    const [cancelRequestError, setCancelRequestError] = useState<string | null>(null);

    // Must include every mutation onSend awaits, or the button re-enables between calls.
    const isBusy = useMemo(
        () =>
            isSubmitNeedsMoreInfoPreviewPending ||
            isSubmitNeedsMoreInfoPending ||
            isSubmitRequestInfoPending ||
            isSubmitApplicationStatusPending,
        [
            isSubmitNeedsMoreInfoPreviewPending,
            isSubmitNeedsMoreInfoPending,
            isSubmitRequestInfoPending,
            isSubmitApplicationStatusPending,
        ],
    );

    const approvedProductSet = useMemo(
        () => new Set(applicantProductNames.filter((p) => getHasEverBeenApproved(p))),
        [applicantProductNames, getHasEverBeenApproved],
    );

    // Warning only, not a block — re-decisioning a "declined as dupe" is sometimes legitimate.
    const declinedSelectedProducts = useMemo(() => {
        if (!applicant) return [];
        const declined: {
            product: ProductNames;
            details: string | null;
            reviewerEmail: string | null;
            dateModified: string;
        }[] = [];
        (selectedProducts as ProductNames[]).forEach((product) => {
            const productData = applicant[ProductNameFields[product]] as
                | FactoringProduct
                | LoadPayProduct
                | FuelProduct
                | undefined;
            const decisionDetail = productData?.mostRecentDecisionDetail;
            if (decisionDetail?.status === DecisionDetailStatuses.DENIED) {
                declined.push({
                    product,
                    details: decisionDetail.details,
                    reviewerEmail: decisionDetail.reviewerEmail,
                    dateModified: decisionDetail.dateModified,
                });
            }
        });
        return declined;
    }, [applicant, selectedProducts]);

    // Warning only, not a block — nudging someone who abandoned mid-application is legitimate.
    const neverSubmittedSelectedProducts = useMemo(() => {
        if (!applicant) return [];
        const neverSubmitted: {
            product: ProductNames;
            startedDate: string | null;
        }[] = [];
        (selectedProducts as ProductNames[]).forEach((product) => {
            if (getHasEverBeenSubmitted(product) || getIsCurrentlyDeclined(product)) return;
            const productData = applicant[ProductNameFields[product]] as
                | FactoringProduct
                | LoadPayProduct
                | FuelProduct
                | undefined;
            neverSubmitted.push({ product, startedDate: productData?.dateModified ?? null });
        });
        return neverSubmitted;
    }, [applicant, selectedProducts, getHasEverBeenSubmitted, getIsCurrentlyDeclined]);

    // Reset on open + apply defaultProduct + pre-select invalid requirements.
    // Runs exactly once per false→true transition of `showDrawer`. Earlier
    // versions keyed this effect on `resolvedCatalog` too, which re-fired the
    // reset whenever the applicant query refetched (every 30s in production
    // via ApplicantContextProvider, plus once on initial mount when
    // `applicantData` resolves separately from `isLoading`). That re-fire
    // wiped the user's `selectedRequirements` and flipped `isMessageEditable`
    // back to false mid-session.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (!showDrawer) {
            wasOpenRef.current = false;
            return;
        }
        if (wasOpenRef.current) return;
        wasOpenRef.current = true;
        if (defaultProduct) {
            // Only the FACTORING_LOADPAY auto-bundle filters nothing-to-review products; a single explicit click always stays selected.
            const products =
                defaultProduct === FACTORING_LOADPAY
                    ? [ProductNames.FACTORING, ProductNames.LOADPAY].filter(
                          (p) => getHasEverBeenSubmitted(p) && !getIsCurrentlyDeclined(p),
                      )
                    : [defaultProduct as ProductNames];
            setValue('product', products, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            });
            if (products.some((p) => getHasEverBeenApproved(p))) {
                setValue('docsOnly', true);
            }
        }
        const preselected = computeInvalidPreselected(resolvedCatalog);
        setSelectedRequirements(preselected);
        selectAllStateRef.current = 'invalid-only';
        setIsMessageEditable(false);
        // Auto-fill effect below will write the textarea contents.
    }, [
        showDrawer,
        defaultProduct,
        resolvedCatalog,
        setValue,
        getHasEverBeenApproved,
        getHasEverBeenSubmitted,
        getIsCurrentlyDeclined,
    ]);

    // Auto-fill the textarea from selected requirements while locked.
    useEffect(() => {
        if (isMessageEditable) return;
        const text = buildBulletText(selectedRequirements, resolvedCatalog, placeholderValues);
        setValue('notes', text, { shouldValidate: false, shouldDirty: true });
    }, [selectedRequirements, isMessageEditable, resolvedCatalog, placeholderValues, setValue]);

    // Once the user has clicked Preview/Send at least once, surface validation
    // errors live as they change selections so the red border / error message
    // updates without requiring another button click.
    useEffect(() => {
        if (formState.submitCount === 0) return;
        trigger();
    }, [selectedRequirements, formState.submitCount, trigger]);

    const productNameOptions = useMemo(
        () =>
            applicantProductNames
                .filter(
                    (p) =>
                        isV2InformationRequestEnabled ||
                        !(p === ProductNames.LOADPAY && getHasEverBeenApproved(p)),
                )
                .map((p) => ({
                    label: p,
                    value: p,
                    disabled: false,
                })),
        [
            applicantProductNames,
            getHasEverBeenApproved,
            isV2InformationRequestEnabled,
            approvedProductSet,
        ],
    );

    const isLoadPayApprovedAndSelected = useMemo(
        () =>
            (selectedProducts ?? []).includes(ProductNames.LOADPAY) &&
            approvedProductSet.has(ProductNames.LOADPAY),
        [selectedProducts, approvedProductSet],
    );

    const isDocsOnlyDisabled = useMemo(() => {
        if (!selectedProducts || selectedProducts.length === 0) return true;
        return isLoadPayApprovedAndSelected;
    }, [selectedProducts, isLoadPayApprovedAndSelected]);

    const trimmedNotes = (notes ?? '').trim();
    const isPreviewStale =
        wasPreviewOpened && trimmedNotes.length > 0 && notes !== lastPreviewedNotes;

    // When notes go empty mid-session, clear the cached preview so the stale
    // "Updating preview…" state doesn't linger over an outdated response.
    // wasPreviewOpened stays true so a fresh selection re-triggers the preview.
    useEffect(() => {
        if (trimmedNotes.length === 0 && previewResponse !== null) {
            setPreviewResponse(null);
            setLastPreviewedNotes(null);
        }
    }, [trimmedNotes, previewResponse]);

    const applyRequirementChange = (next: RequirementId[]) => {
        setSelectedRequirements(next);
    };

    const handleRequirementToggle = (id: RequirementId) => {
        setHasUserInteracted(true);
        if (isMessageEditable) {
            setPendingCheckboxToggle(id);
            return;
        }
        const has = selectedRequirements.includes(id);
        applyRequirementChange(
            has ? selectedRequirements.filter((x) => x !== id) : [...selectedRequirements, id],
        );
    };

    const confirmCheckboxOverwrite = () => {
        if (!pendingCheckboxToggle) return;
        setHasUserInteracted(true);
        const id = pendingCheckboxToggle;
        const has = selectedRequirements.includes(id);
        const next = has
            ? selectedRequirements.filter((x) => x !== id)
            : [...selectedRequirements, id];
        // Continue keeps the user in edit mode; auto-fill effect skips them, so
        // we must rewrite the textarea explicitly here from the new selection.
        setSelectedRequirements(next);
        setValue('notes', buildBulletText(next, resolvedCatalog, placeholderValues), {
            shouldValidate: false,
            shouldDirty: true,
        });
        setPendingCheckboxToggle(null);
    };

    const cancelCheckboxOverwrite = () => setPendingCheckboxToggle(null);

    const handleSelectAllToggle = () => {
        setHasUserInteracted(true);
        const nextState: SelectAllState =
            selectAllStateRef.current === 'all' ? 'invalid-only' : 'all';
        selectAllStateRef.current = nextState;
        const next =
            nextState === 'all'
                ? resolvedCatalog.map((r) => r.id)
                : computeInvalidPreselected(resolvedCatalog);
        if (isMessageEditable) {
            // Same overwrite policy — Select All in edit mode requires confirmation.
            // Use a single confirm flow modeled as a generic id "all" / "invalid-only"
            // surfaced via the same modal would complicate state; instead keep the
            // simpler rule: Select All in edit mode is blocked by the per-checkbox
            // confirm pattern. Reset to locked state via another open. For now we
            // simply rewrite the textarea after applying the selection — the user
            // already opted out of bullet sync by editing.
            setSelectedRequirements(next);
            setValue('notes', buildBulletText(next, resolvedCatalog, placeholderValues), {
                shouldValidate: false,
                shouldDirty: true,
            });
            return;
        }
        applyRequirementChange(next);
    };

    const handleClickToEditMessage = () => {
        if (isMessageEditable) return;
        setHasUserInteracted(true);
        setIsMessageEditable(true);
    };

    const handleStopEditing = () => {
        if (!isMessageEditable) return;
        setHasUserInteracted(true);
        // Auto-fill effect rewrites notes from selectedRequirements once
        // isMessageEditable flips back to false.
        setIsMessageEditable(false);
    };

    const handleProductChange = (e: { value: string[] }) => {
        setHasUserInteracted(true);
        setValue('product', e.value, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });
        if (e.value.some((p) => approvedProductSet.has(p as ProductNames))) {
            setValue('docsOnly', true);
        }
    };

    const handleDocsOnlyChange = () => {
        setHasUserInteracted(true);
        setValue('docsOnly', !docsOnly);
    };

    const closeDrawer = () => {
        setShowDrawer(false);
        setPreviewResponse(null);
        setLastPreviewedNotes(null);
        setWasPreviewOpened(false);
        setSelectedRequirements([]);
        setIsMessageEditable(false);
        setHasUserInteracted(false);
        setPendingCancelRequestId(null);
        setCancelRequestError(null);
        selectAllStateRef.current = 'invalid-only';
        reset();
    };

    const handleCancelRequestClick = (informationRequestId: string) => {
        setCancelRequestError(null);
        setPendingCancelRequestId(informationRequestId);
    };

    const handleDismissCancelRequest = () => {
        setPendingCancelRequestId(null);
        setCancelRequestError(null);
    };

    const handleConfirmCancelRequest = () => {
        if (!applicantId || !pendingCancelRequestId) return;
        cancelInformationRequest(
            { applicantId, informationRequestId: pendingCancelRequestId },
            {
                onSuccess: () => {
                    setPendingCancelRequestId(null);
                    setCancelRequestError(null);
                },
                onError: () => {
                    setCancelRequestError(
                        'Failed to cancel the information request. Please try again.',
                    );
                },
            },
        );
    };

    const handleCancelClick = () => {
        if (hasUserInteracted) {
            setShowDiscardConfirm(true);
            return;
        }
        closeDrawer();
    };

    const handleConfirmDiscard = () => {
        setShowDiscardConfirm(false);
        closeDrawer();
    };

    const onPreview = async (formData: { product: string[]; notes: string; docsOnly: boolean }) => {
        if (!applicantId || isSubmitNeedsMoreInfoPreviewPending) return;
        const resp = await submitNeedsMoreInfoPreview({
            applicantId,
            products: formData.product as ProductNames[],
            notes: formData.notes,
            docsOnly: !!formData.docsOnly,
        });
        if (!resp?.body) {
            setPreviewResponse(null);
            return;
        }
        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(resp.body, 'text/html');
        parsedHtml.querySelectorAll('a').forEach((anchor) => {
            if (anchor.textContent?.trim() === 'Complete Application') {
                anchor.removeAttribute('href');
                anchor.style.pointerEvents = 'none';
            }
        });
        const cleanHtml = isSafeEmailViewsEnabled
            ? DOMPurify.sanitize(parsedHtml.documentElement.outerHTML, { WHOLE_DOCUMENT: true })
            : DOMPurify.sanitize(parsedHtml.body.innerHTML);
        setPreviewResponse({
            subject: resp.subject,
            from: resp.from,
            to: resp.to,
            cc: resp.cc,
            bcc: resp.bcc,
            body: cleanHtml,
        });
        setLastPreviewedNotes(formData.notes);
        setWasPreviewOpened(true);
    };

    const onSend = async (formData: { product: string[]; notes: string; docsOnly: boolean }) => {
        if (!applicantId || isBusy) {
            return;
        }

        const selectedProductNames = formData.product as ProductNames[];
        const approvedSelected = selectedProductNames.filter((p) => approvedProductSet.has(p));
        const unapprovedSelected = selectedProductNames.filter((p) => !approvedProductSet.has(p));

        if (isV2InformationRequestEnabled && approvedSelected.length > 0) {
            await withRetry(() =>
                submitRequestInfo({
                    applicantId,
                    moreInfoType: formData.docsOnly ? MoreInfoType.DocsOnly : MoreInfoType.FullApp,
                    moreInfoMessage: formData.notes,
                    isInternal: false,
                    products: approvedSelected,
                }),
            );
        }

        if (isV2InformationRequestPreApprovalEnabled && unapprovedSelected.length > 0) {
            await withRetry(() =>
                submitRequestInfo({
                    applicantId,
                    moreInfoType: formData.docsOnly ? MoreInfoType.DocsOnly : MoreInfoType.FullApp,
                    moreInfoMessage: formData.notes,
                    isInternal: false,
                    products: unapprovedSelected,
                }),
            );
            await Promise.all(
                unapprovedSelected.map((product) =>
                    withRetry(() =>
                        submitApplicationStatus({
                            applicantId,
                            product,
                            status: DecisionDetailStatuses.NEEDMOREINFO,
                            notes: formData.notes,
                        }),
                    ),
                ),
            );
        } else if (unapprovedSelected.length > 0) {
            await submitNeedsMoreInfo({
                applicantId,
                products: unapprovedSelected,
                notes: formData.notes,
                docsOnly: !!formData.docsOnly,
            });
        }

        queryClient.invalidateQueries({ queryKey: ['applicant-activity', applicantId] });
        queryClient.invalidateQueries({ queryKey: ['applicant', applicantId] });
        closeDrawer();
    };

    // Auto-rerun the preview when it's open and the message changes. Debounced so
    // typing in the textarea doesn't fire a request on every keystroke.
    useEffect(() => {
        if (!isPreviewStale) return;
        if (isSubmitNeedsMoreInfoPreviewPending) return;
        const handle = setTimeout(() => {
            handleSubmit(onPreview)();
        }, 500);
        return () => clearTimeout(handle);
    }, [isPreviewStale, isSubmitNeedsMoreInfoPreviewPending, notes]);

    const selectedCount = selectedRequirements.length;

    // The footer's primary button is a single Preview→Send toggle (mirrors the
    // old RequestInfoModal): it reads "Preview Email" until a preview is open,
    // then flips to "Send Request (n)". An open preview = a non-empty body.
    const isPreviewOpen = !!previewResponse?.body;

    // Bring the email preview into view once it opens so the user notices it —
    // the drawer is a floating panel on the right and the preview renders below
    // the fold. Guarded because scrollIntoView is absent in jsdom.
    const previewRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (isPreviewOpen) {
            previewRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        }
    }, [isPreviewOpen]);

    const handlePrimaryClick = () => {
        if (isPreviewOpen) {
            handleSubmit(onSend)();
        } else {
            handleSubmit(onPreview)();
        }
    };

    const handleHidePreview = () => {
        setPreviewResponse(null);
        setLastPreviewedNotes(null);
        setWasPreviewOpened(false);
    };

    const footer = (
        <>
            <Button
                variant={ButtonVariants.secondary}
                size={ButtonSizes.small}
                onClick={handleCancelClick}
                data-testid="request-info-drawer-cancel"
                disabled={isBusy}
            >
                Cancel
            </Button>
            <Button
                variant={ButtonVariants.primary}
                size={ButtonSizes.small}
                onClick={handlePrimaryClick}
                data-testid={
                    isPreviewOpen ? 'request-info-drawer-send' : 'request-info-drawer-preview'
                }
                disabled={isBusy}
                loading={isBusy}
            >
                {isPreviewOpen ? `Send Request (${selectedCount})` : 'Preview Email'}
            </Button>
        </>
    );

    return (
        <>
            <SideDrawer
                isOpen={showDrawer}
                onClose={handleCancelClick}
                title="Request Information"
                position="right"
                modal={false}
                widthPx={500}
                pushPageContent
                closeLabel="Close request information drawer"
                bodyTestId="request-info-drawer"
                scrollHintTestId="request-info-drawer-scroll-hint"
                scrollHintLabel="Scroll for more"
                footer={footer}
            >
                {activeRequests.length > 0 ? (
                    <ActiveRequestsSection>
                        <SectionHeader>✉️ Active Requests ({activeRequests.length})</SectionHeader>
                        {activeRequests.map((r) => (
                            <ActiveRequestCard
                                key={r.id}
                                data-testid={`active-request-card-${r.id}`}
                            >
                                <ActiveRequestMeta>
                                    <ActiveRequestProductTag>{r.product}</ActiveRequestProductTag>
                                    <ActiveRequestDate>
                                        Sent {new Date(r.dateCreated).toLocaleDateString()}
                                    </ActiveRequestDate>
                                    {r.isPendingApplicantResponse ? (
                                        <ActiveRequestDate>· Awaiting response</ActiveRequestDate>
                                    ) : null}
                                    {isCancelInformationRequestEnabled ? (
                                        <PrimeButton
                                            icon="pi pi-trash"
                                            severity="danger"
                                            text
                                            rounded
                                            size="small"
                                            aria-label={`Cancel ${r.product} information request`}
                                            data-testid={`cancel-information-request-btn-${r.id}`}
                                            onClick={() => handleCancelRequestClick(r.id)}
                                            style={{ marginLeft: 'auto' }}
                                        />
                                    ) : null}
                                </ActiveRequestMeta>
                                {r.moreInfoMessage ? (
                                    <ActiveRequestMessage>{r.moreInfoMessage}</ActiveRequestMessage>
                                ) : null}
                            </ActiveRequestCard>
                        ))}
                    </ActiveRequestsSection>
                ) : null}

                <SendNewHeader>
                    <SectionHeader>Send New Request</SectionHeader>
                </SendNewHeader>

                <ProductSection>
                    <SectionLabel htmlFor="multiselect-product-status">
                        Select Product Application
                    </SectionLabel>
                    <MultiSelect
                        id="multiselect-product-status"
                        value={selectedProducts}
                        options={productNameOptions}
                        onChange={handleProductChange}
                        placeholder="Select Product Application(s)"
                        display="chip"
                        data-testid="multiselect-product-status"
                        data-value={selectedProducts.join(',')}
                        showSelectAll={false}
                        showClear={false}
                    />
                    <Checkbox
                        id="docsOnly"
                        name="docsOnly"
                        data-testid="docs-only-checkbox"
                        checkboxContainerTestId="docs-only-checkbox"
                        checked={docsOnly}
                        onChange={handleDocsOnlyChange}
                        disabled={isDocsOnlyDisabled}
                    >
                        Only Allow Document Uploads. Do not allow any Application edits.
                    </Checkbox>
                    {productError?.message ? (
                        <FieldError data-testid="product-error">{productError.message}</FieldError>
                    ) : null}
                    {declinedSelectedProducts.length > 0 ? (
                        <DeclinedProductWarning data-testid="declined-product-warning">
                            {declinedSelectedProducts.map(
                                ({ product, details, reviewerEmail, dateModified }) => (
                                    <DeclinedProductWarningRow key={product}>
                                        <span>
                                            ⚠️ FYI, {getProductLabel(product)} was already declined
                                            {reviewerEmail ? ` by ${reviewerEmail}` : ''}
                                            {details ? ` due to ${details}` : ''}.
                                        </span>
                                        <DeclinedProductWarningMeta>
                                            Declined {formatDateWithTimezone(dateModified)}
                                        </DeclinedProductWarningMeta>
                                    </DeclinedProductWarningRow>
                                ),
                            )}
                        </DeclinedProductWarning>
                    ) : null}
                    {neverSubmittedSelectedProducts.length > 0 ? (
                        <DeclinedProductWarning data-testid="never-submitted-product-warning">
                            {neverSubmittedSelectedProducts.map(({ product, startedDate }) => (
                                <DeclinedProductWarningRow key={product}>
                                    <span>
                                        ⚠️ FYI, {getProductLabel(product)} has no submitted
                                        application — the applicant{' '}
                                        {startedDate
                                            ? `started it on ${formatDateWithTimezone(startedDate)} but never submitted it`
                                            : 'never started it'}
                                        .
                                    </span>
                                </DeclinedProductWarningRow>
                            ))}
                        </DeclinedProductWarning>
                    ) : null}
                </ProductSection>

                <div>
                    <SectionHeaderRow>
                        <SectionHeader>Requirements</SectionHeader>
                        <SelectAllLink
                            type="button"
                            onClick={handleSelectAllToggle}
                            data-testid="requirements-select-all"
                        >
                            Select All
                        </SelectAllLink>
                    </SectionHeaderRow>
                    {isCatalogLoading ? (
                        <div data-testid="requirements-loading">
                            <LoadingIndicator show />
                        </div>
                    ) : null}
                    {isCatalogError ? (
                        <FieldError data-testid="requirements-error">
                            Couldn't load requirements. You can still write the message yourself.{' '}
                            <SelectAllLink
                                type="button"
                                onClick={refetchCatalog}
                                data-testid="requirements-retry"
                            >
                                Retry
                            </SelectAllLink>
                        </FieldError>
                    ) : null}
                    {resolvedCatalog.length > 0 ? (
                        <RequirementList>
                            {resolvedCatalog.map((r) => {
                                const checked = selectedRequirements.includes(r.id);
                                return (
                                    <RequirementRow
                                        key={r.id}
                                        checked={checked}
                                        data-testid={`requirement-row-${r.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => handleRequirementToggle(r.id)}
                                            data-testid={`requirement-checkbox-${r.id}`}
                                            aria-label={r.label}
                                        />
                                        <RequirementLabel>{r.label}</RequirementLabel>
                                        <StatusBadge
                                            tone={STATUS_TONE[r.status]}
                                            data-testid={`requirement-status-${r.id}`}
                                        >
                                            {STATUS_LABEL[r.status]}
                                        </StatusBadge>
                                    </RequirementRow>
                                );
                            })}
                        </RequirementList>
                    ) : null}
                </div>

                <div>
                    <SectionHeaderRow>
                        <SectionHeader>Message Preview</SectionHeader>
                        <SelectedCountBadge data-testid="selected-count-badge">
                            {selectedCount} selected
                        </SelectedCountBadge>
                    </SectionHeaderRow>
                    <MessageTextArea
                        data-testid="message-textarea"
                        value={notes ?? ''}
                        disabled={!isMessageEditable}
                        hasError={!!notesError?.message}
                        onChange={(e) => {
                            setHasUserInteracted(true);
                            onNotesChange(e.target.value);
                        }}
                        rows={6}
                        spellCheck
                        placeholder={
                            selectedCount === 0
                                ? 'Select requirements above to build the message.'
                                : ''
                        }
                    />
                    {notesError?.message ? (
                        <FieldError data-testid="notes-error">{notesError.message}</FieldError>
                    ) : null}
                    {isMessageEditable ? (
                        <EditMessageLink
                            type="button"
                            onClick={handleStopEditing}
                            data-testid="stop-editing-message"
                        >
                            Stop editing (revert to auto-generated message)
                        </EditMessageLink>
                    ) : (
                        <EditMessageLink
                            type="button"
                            onClick={handleClickToEditMessage}
                            data-testid="click-to-edit-message"
                        >
                            Click to edit message
                        </EditMessageLink>
                    )}
                </div>

                <div>
                    <SectionHeader style={{ marginBottom: 8 }}>Send To</SectionHeader>
                    <ReadOnlyEmailRow data-testid="send-to-email">
                        <span>Email</span>
                        {applicant?.email ?? ''}
                    </ReadOnlyEmailRow>
                </div>

                {isSubmitNeedsMoreInfoPreviewPending ? (
                    <PreviewSection data-testid="preview-loading">
                        <LoadingIndicator show />
                    </PreviewSection>
                ) : previewResponse?.body ? (
                    <PreviewSection
                        ref={previewRef}
                        stale={isPreviewStale}
                        data-testid="email-preview-section"
                    >
                        <PreviewSectionHeader>
                            <SectionHeader>Email Preview</SectionHeader>
                            {isPreviewStale ? (
                                <StalePreviewBadge data-testid="email-preview-stale-badge">
                                    Updating preview…
                                </StalePreviewBadge>
                            ) : null}
                        </PreviewSectionHeader>
                        <EmailPreviewHeaders data-testid="request-info-email-preview-headers">
                            {previewResponse.subject ? (
                                <h3>
                                    Subject: <span>{previewResponse.subject}</span>
                                </h3>
                            ) : null}
                            {previewResponse.from ? (
                                <h3>
                                    From: <span>{previewResponse.from}</span>
                                </h3>
                            ) : null}
                            {previewResponse.to ? (
                                <h3>
                                    To: <span>{previewResponse.to}</span>
                                </h3>
                            ) : null}
                            {previewResponse.cc?.length ? (
                                <h3>
                                    CC: <span>{previewResponse.cc.join(', ')}</span>
                                </h3>
                            ) : null}
                            {previewResponse.bcc?.length ? (
                                <h3>
                                    BCC: <span>{previewResponse.bcc.join(', ')}</span>
                                </h3>
                            ) : null}
                        </EmailPreviewHeaders>
                        {isSafeEmailViewsEnabled ? (
                            <EmailPreviewFrame html={previewResponse.body} />
                        ) : (
                            <EmailPreviewBody
                                data-testid="request-info-email-preview-body"
                                dangerouslySetInnerHTML={{ __html: previewResponse.body }}
                            />
                        )}
                        <HidePreviewLink
                            type="button"
                            onClick={handleHidePreview}
                            data-testid="hide-preview-link"
                        >
                            Hide preview
                        </HidePreviewLink>
                    </PreviewSection>
                ) : null}
            </SideDrawer>

            <Modal
                id="request-info-drawer-discard-confirm"
                visible={showDiscardConfirm}
                headerContent="Discard changes?"
                bodyContent={
                    <p>
                        Closing now will discard your selections and message. Are you sure you want
                        to continue?
                    </p>
                }
                onClose={() => setShowDiscardConfirm(false)}
                primaryButtonText="Discard"
                onPrimaryAction={handleConfirmDiscard}
                primaryAdditionalProps={{ 'data-testid': 'discard-confirm-primary' } as never}
                secondaryButtonText="Keep editing"
                onSecondaryAction={() => setShowDiscardConfirm(false)}
                secondaryAdditionalProps={{ 'data-testid': 'discard-confirm-secondary' } as never}
            />

            <Modal
                id="request-info-drawer-overwrite-confirm"
                visible={pendingCheckboxToggle !== null}
                headerContent="Overwrite your message?"
                bodyContent={
                    <p>
                        Editing requirements will overwrite your message with the auto-generated
                        text. Continue?
                    </p>
                }
                onClose={cancelCheckboxOverwrite}
                primaryButtonText="Continue"
                onPrimaryAction={confirmCheckboxOverwrite}
                primaryAdditionalProps={{ 'data-testid': 'overwrite-confirm-primary' } as never}
                secondaryButtonText="Cancel"
                onSecondaryAction={cancelCheckboxOverwrite}
                secondaryAdditionalProps={{ 'data-testid': 'overwrite-confirm-secondary' } as never}
            />

            <Modal
                id="cancel-information-request-confirm"
                visible={pendingCancelRequestId !== null}
                headerContent="Cancel this information request?"
                bodyContent={
                    <>
                        <p>
                            The applicant will not be notified. No email, notification, or
                            in-app message will be sent.
                        </p>
                        {cancelRequestError ? (
                            <FieldError data-testid="cancel-information-request-error">
                                {cancelRequestError}
                            </FieldError>
                        ) : null}
                    </>
                }
                onClose={handleDismissCancelRequest}
                primaryButtonText="Cancel Request"
                onPrimaryAction={handleConfirmCancelRequest}
                primaryAdditionalProps={
                    {
                        'data-testid': 'cancel-information-request-confirm-primary',
                        loading: isCancelingInformationRequest,
                        disabled: isCancelingInformationRequest,
                    } as never
                }
                secondaryButtonText="Keep Request"
                onSecondaryAction={handleDismissCancelRequest}
                secondaryAdditionalProps={
                    { 'data-testid': 'cancel-information-request-confirm-secondary' } as never
                }
            />
        </>
    );
};
